import _ from 'lodash';
import Logger from 'logger';
import { API_V1 } from './APIs/APIv1';

export const getContacts = async (
  page?: number,
  total?: number,
  logic?: string,
  filter?: string,
  search?: string
) => {
  try {
    const params: any = {};

    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }

    if (!_.isNil(logic) && !_.isNil(filter)) {
      params.logic = logic;
      params.filter = filter;
    }

    if (!_.isNil(search) && search !== '') {
      params.search = search;
    }

    const response = await API_V1.get('/contact-management/contacts', {
      params,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getContactById = async (contactId: string) => {
  try {
    const response = await API_V1.get(`/contact-management/contacts/${contactId}`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching interaction history for contact %s!', contactId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactById = async (
  contactId: string,
  payload: { firstname?: string; lastname?: string; sex?: string; manualTags?: string[]; email?: string }
) => {
  try {
    const response = await API_V1.put(`/contact-management/contacts/${contactId}`, payload);

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching interaction history for contact %s!', contactId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getInteractionHistoryById = async (contactId: string) => {
  try {
    const response = await API_V1.get(`/contact-management/contacts/${contactId}/interaction-history`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching contact %s!', contactId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getAnalysisResultsById = async (contactId: string, lang: string) => {
  try {
    const response = await API_V1.get(`/contact-management/contacts/${contactId}/analysis`, {
      params: { lang },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching analysis results for contact %s!', contactId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getManualTags = async (page?: number, total?: number) => {
  try {
    const params: any = {};

    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }

    const response = await API_V1.get('/contact-management/manual-tags', {
      params,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching all manual tags!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCampaignTags = async (page?: number, total?: number) => {
  try {
    const params: any = {};

    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }

    const response = await API_V1.get('/contact-management/contacts/campaign-tags', {
      params,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching all campaign tags!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createManualTag = async (name: string) => {
  try {
    const response = await API_V1.post('/contact-management/manual-tags', {
      name,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while creating manual tag!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateManualTag = async (tagId: string, name: string) => {
  try {
    const response = await API_V1.put(`/contact-management/manual-tags/${tagId}`, {
      name,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating manual tag %s!', tagId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteManualTag = async (tagId: string) => {
  try {
    const response = await API_V1.delete(`/contact-management/manual-tags/${tagId}`);

    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting manual tag %s!', tagId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const addTagsToContacts = async (tags: string[], contacts: string[]) => {
  try {
    const response = await API_V1.put('/contact-management/contacts/add-tags', {
      tags,
      contacts,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while adding tags to contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getPipelineKpis = async () => {
  try {
    const response = await API_V1.get('/contact-management/pipeline-kpis');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching pipeline KPIs!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: null };
  }
};

export const updatePipelineStatus = async (contactIds: string[], pipelineStatus: string) => {
  try {
    const response = await API_V1.put('/contact-management/contacts/pipeline-status', {
      contactIds,
      pipelineStatus,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while updating pipeline status!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: null };
  }
};

export const getAnsweredContacts = async () => {
  try {
    const response = await API_V1.get('/contact-management/campaigns/answered-contacts');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching answered contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
